import {
  compact,
  filter,
  find,
  first,
  flatMap,
  forEach,
  includes,
  map,
  some
} from 'lodash'
import menuRoutes, {
  MenuRoute,
  MenuRouteGroup,
  MenuRouteGroupName,
  Collapsible
} from '../routes/menuRoutes'

interface MenuCollectPermittedCombinedSubsidiaries {
  (type: MenuRouteGroupName, permittedModules: string[]): MenuRoute[] | undefined
}

interface MenuCollectPermittedSubsidiaries {
  (type: MenuRouteGroupName, permittedModules: string[]): (MenuRoute | Collapsible)[]
}

function getCombinedSubsidiaries (name: MenuRouteGroupName): MenuRoute[] {
  const group = find(menuRoutes, { name })
  if (!group) return []
  const collapsibles = filter(group.subsidiaries, sub => {
    return 'subsidiaries' in sub
  }) as Collapsible[]
  const routes = filter(group.subsidiaries, sub => {
    return 'to' in sub
  }) as MenuRoute[]
  const collapsibleRoutes = flatMap(collapsibles, collapsible => {
    return collapsible.subsidiaries
  })
  return [...routes, ...collapsibleRoutes]
}

const collectPermittedSubsidiaries: MenuCollectPermittedSubsidiaries = (type, permittedModules) => {
  const group = find(menuRoutes, { name: type })
  if (group) {
    const subsidiaries = filter(group.subsidiaries, subsidiary => {
      if ('subsidiaries' in subsidiary) return subsidiary
      const { name } = subsidiary
      return includes(permittedModules, name)
    }) as (MenuRoute | Collapsible)[]
    map(subsidiaries, sub => {
      if ('subsidiaries' in sub) {
        sub.subsidiaries = filter(sub.subsidiaries, sub => {
          const { name } = sub
          return includes(permittedModules, name)
        })
      }
      return sub
    })
    return subsidiaries
  } else return []
}

const collectPermittedCombinedSubsidiaries: MenuCollectPermittedCombinedSubsidiaries = (type, permittedModules) => {
  const subsidiaries = getCombinedSubsidiaries(type)
  if (subsidiaries.length) {
    const permittedSubsidiaries = filter(subsidiaries, subsidiary => {
      const { name } = subsidiary
      return includes(permittedModules, name)
    })
    return permittedSubsidiaries
  }
}

export default {
  collectPermittedCombinedSubsidiaries,
  collectPermittedSubsidiaries,
  getCombinedSubsidiaries,

  isGroupPath (groupPath: string, path: string) {
    const group = find(menuRoutes, { to: groupPath })
    if (!group) return false
    const subsidiary = find(group.subsidiaries, sub => {
      if ('to' in sub) {
        return sub.to === path
      } else if ('subsidiaries' in sub) {
        return some(sub.subsidiaries, sub => sub.to === path)
      }
    })
    return Boolean(subsidiary)
  },

  toGroupColumns (columns: number[]) {
    let columnIndex = 0
    const groupColumns: MenuRouteGroup[][] = []
    forEach(columns, () => {
      let rowIndex = columnIndex
      const groups: MenuRouteGroup[] = []
      forEach(menuRoutes, () => {
        const group = menuRoutes[rowIndex]
        if (!group) return false
        groups.push(group)
        rowIndex += columns.length
      })
      groupColumns.push(groups)
      columnIndex += 1
    })
    return groupColumns
  },

  groupFromPath (path: string): MenuRouteGroup | null {
    const group = find(menuRoutes, route => {
      return find(route.subsidiaries, sub => {
        if ('to' in sub) {
          return sub.to === path
        } else if ('subsidiaries' in sub) {
          return some(sub.subsidiaries, sub => sub.to === path)
        }
      })
    }) as MenuRouteGroup | undefined
    return group || null
  },

  getCurrentRouteName () {
    const { pathname } = window.location
    const routeNames = map(menuRoutes, routeGroup => {
      const subsidiaries = map(routeGroup.subsidiaries, route => {
        if ('to' in route && route.to === pathname) return route
      })
      const routes = compact(subsidiaries)
      const route = first(routes)
      if (route) return route.name
    })
    const compactedRouteNames = compact(routeNames)
    return first(compactedRouteNames)
  }
}
